import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import GlobalDeleteModal from "../../components/partials/GlobalDeleteModal";
import BannerAPI from "../../api/BannerApi";
import { showError, showSuccess } from "../../utils/toastUtils";
import { useLoader } from "../../utils/LoaderUtils";

import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";

import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";

import TableShimmer from "../../components/partials/TableShimmer";
import RowsPerPage from "../../components/partials/RowsPerPage";
import Pagination from "../../components/partials/Pagination";
import SortableRow from "./SortableRow";
import useQueryFilters from "../../hooks/useQueryFilters";
import useDeleteItem from "../../hooks/useDeleteItem";

function List() {
  const { showLoader, hideLoader } = useLoader();
  const [tableLoading, setTableLoading] = useState(false);

  const [items, setItems] = useState([]);
  const [pagination, setPagination] = useState({});

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 1,
      },
    }),
  );

  /* =============  FETCH API DATA ================ */
  const { updateFilter, searchParams } = useQueryFilters();

  const fetchBannerData = useCallback(async () => {
    setTableLoading(true);
    setItems([]);

    try {
      const res = await BannerAPI.getDetails(searchParams);

      if (res.code === 200) {
        const result = res.data;

        setItems(result.banners);
        setPagination(result.pagination);
      }
    } catch (err) {
    } finally {
      setTableLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    fetchBannerData();
  }, [fetchBannerData]);

  const { isDeleting, openDelete, handleDelete } = useDeleteItem(
    BannerAPI.deleteItem,
    fetchBannerData,
  );

  /* =============  STATUS CHANGE API ================ */
  const handleStatusChange = useCallback(async (id, status) => {
    showLoader();

    try {
      const res = await BannerAPI.updateStatus(id);

      if (res.code === 200) {
        hideLoader();
        showSuccess("Status updated successfully");

        setItems((prev) =>
          prev.map((item) => (item._id === id ? { ...item, status } : item)),
        );
      } else {
        showError("Failed to update status");
      }
    } catch (err) {
      hideLoader();
      showError("Something went wrong");
    }
  });

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (active.id !== over.id) {
      const oldIndex = items.findIndex((i) => i._id === active.id);
      const newIndex = items.findIndex((i) => i._id === over.id);

      const newItems = arrayMove(items, oldIndex, newIndex);
      setItems(newItems);

      const updatedOrder = newItems.map((item, index) => ({
        id: item._id,
        sort_order: index + 1,
      }));

      try {
        const res = await BannerAPI.updateOrder(updatedOrder);
        if (res.code === 200) {
          showSuccess(res.message);
        }
      } catch (error) {
        console.error("Order update failed", error);
      }
    }
  });

  return (
    <>
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Manage Store Banners</h4>
              <h6>
                Upload and organize promotional banners for your store’s
                homepage and other sections.
              </h6>
            </div>
          </div>
          <div className="page-btn">
            <Link to="/banners/add-new" className="btn btn-primary">
              <i className="ti ti-circle-plus me-1" />
              Add Banner
            </Link>
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <h4>Banner Listing</h4>
          </div>
          <div className="card-body p-0">
            <div className="row">
              <div className="col-md-12">
                <div className="countablepagination p-2">
                  <p>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{" "}
                    {Math.min(
                      pagination.page * pagination.limit,
                      pagination.total,
                    )}{" "}
                    of {pagination.total} results
                  </p>
                </div>
              </div>
            </div>
            <div className="table-responsive">
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <table className="table w-100">
                  <thead className="thead-light">
                    <tr>
                      <th>S. No.</th>
                      <th>Banner</th>
                      <th>Headings</th>
                      <th>Redirect Url</th>
                      <th>Created At</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableLoading ? (
                      <TableShimmer rows={10} cols={6} />
                    ) : items.length > 0 ? (
                      <SortableContext
                        items={items.map((i) => i._id)}
                        strategy={verticalListSortingStrategy}
                      >
                        {items.map((item, index) => (
                          <SortableRow
                            key={item._id}
                            item={item}
                            index={index}
                            page={pagination?.page}
                            limit={pagination?.limit}
                            handleStatusChange={handleStatusChange}
                            openDelete={openDelete}
                          />
                        ))}
                      </SortableContext>
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center p-4 text-muted">
                          No Banner Added,{" "}
                          <Link to="/brand/add-new" className="text-primary">
                            Click Here{" "}
                          </Link>
                          to add new banner
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </DndContext>
            </div>

            <div className="row">
              <RowsPerPage
                value={searchParams.get("limit") || "10"}
                onChange={(val) => updateFilter("limit", val)}
              />

              <Pagination pagination={pagination} updateFilter={updateFilter} />
            </div>
          </div>
        </div>
      </div>

      <GlobalDeleteModal onDeleted={handleDelete} isDeleting={isDeleting} />
    </>
  );
}

export default List;
